/**
 * SubtaskCard — renders the subtask list for a reviewed module.
 *
 * SRP: pure presentation. Takes the subtasks array from an
 * AuditRecord (plus parse_status) and renders a compact list of
 * subtasks with their stable id + title + order. No fetching, no
 * state, no Word interaction.
 *
 * IA-agnostic: both the classic tabbed UI (mount inside
 * SectionDetailPanel) and the chat-first UI (render as an inline
 * chat card in ChatFeedRenderer) consume the same component.
 *
 * Visual tiers:
 *   - parse_status == "ok":     subtask list
 *   - parse_status == "failed": single amber banner "Subtask
 *                               structure couldn't be parsed"
 *   - parse_status == "skipped_error_draft": neutral info banner
 *
 * Spec: docs/specs/10-review-agent-sidecar.md § Audit Record Shape.
 */

import { Caption1, Text, makeStyles, tokens } from "@fluentui/react-components";
import type React from "react";
import type { ParseStatus, SubtaskAuditEntry } from "./types";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  header: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
  },
  headerTitle: {
    fontWeight: tokens.fontWeightSemibold,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    margin: 0,
    padding: 0,
    listStyle: "none",
  },
  item: {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    columnGap: tokens.spacingHorizontalS,
    alignItems: "baseline",
  },
  index: {
    fontFamily: tokens.fontFamilyMonospace,
    color: tokens.colorNeutralForeground3,
    minWidth: "2ch",
    textAlign: "right",
  },
  title: {
    color: tokens.colorNeutralForeground1,
  },
  parseFailed: {
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorPaletteYellowBackground2,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorPaletteYellowBorderActive}`,
  },
  skippedError: {
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
});

export interface SubtaskCardProps {
  moduleId: string;
  subtasks: SubtaskAuditEntry[];
  parseStatus: ParseStatus;
  /** Present when ``parseStatus === "failed"`` — e.g.
   * ``["missing_manifest_fence:no triple-backtick fence found"]``. */
  parseErrors?: string[];
}

export const SubtaskCard: React.FC<SubtaskCardProps> = ({ moduleId, subtasks, parseStatus, parseErrors }) => {
  const styles = useStyles();

  if (parseStatus === "failed") {
    return (
      <div className={styles.parseFailed} aria-live="polite">
        <Text weight="semibold">Subtask structure couldn&apos;t be parsed</Text>
        <Caption1 block>
          Module <code>{moduleId}</code> didn&apos;t follow the subtask protocol. Review the audit record for details.
        </Caption1>
        {parseErrors && parseErrors.length > 0 && (
          <ul>
            {parseErrors.map((err, i) => (
              <li key={`err-${i}-${err.slice(0, 32)}`}>
                <Caption1>{err}</Caption1>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  if (parseStatus === "skipped_error_draft") {
    return (
      <div className={styles.skippedError} aria-live="polite">
        <Text weight="semibold">Draft skipped</Text>
        <Caption1 block>
          Module <code>{moduleId}</code> draft began with an error marker and was not reviewed.
        </Caption1>
      </div>
    );
  }

  // parseStatus === "ok"
  if (subtasks.length === 0) {
    return (
      <div className={styles.root}>
        <Text italic>No subtasks recorded for {moduleId}.</Text>
      </div>
    );
  }

  return (
    <section className={styles.root} aria-label={`Subtasks for ${moduleId}`}>
      <header className={styles.header}>
        <Text className={styles.headerTitle}>Subtasks</Text>
        <Caption1>
          {subtasks.length} subtask{subtasks.length === 1 ? "" : "s"} in {moduleId}
        </Caption1>
      </header>
      <ol className={styles.list}>
        {subtasks
          .slice()
          .sort((a, b) => a.order_index - b.order_index)
          .map((task) => (
            <li className={styles.item} key={task.id}>
              <span className={styles.index}>{String(task.order_index + 1).padStart(2, "0")}.</span>
              <span className={styles.title} title={task.id}>
                {task.title}
              </span>
            </li>
          ))}
      </ol>
    </section>
  );
};
